import React from 'react';
import { Item } from '../lib/schema';
import { formatPrice, calculateMargin, calculateMarginPercent } from '../lib/pricing';

interface SalesTableProps {
  items: Item[];
}

const SalesTable: React.FC<SalesTableProps> = ({ items }) => {
  const sortedItems = [...items].sort((a, b) => 
    new Date(b.sold?.sellDate || '').getTime() - new Date(a.sold?.sellDate || '').getTime()
  );

  const totalRevenue = items.reduce((sum, item) => sum + (item.sold?.sellPrice || 0), 0);
  const totalCost = items.reduce((sum, item) => sum + item.buyPrice, 0);
  const totalProfit = totalRevenue - totalCost;
  const avgMargin = totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="glass-effect p-4">
          <div className="text-center">
            <p className="text-gray-400 text-sm">Total Revenue</p>
            <p className="text-2xl font-bold text-white">{formatPrice(totalRevenue)}</p>
          </div>
        </div>
        <div className="glass-effect p-4">
          <div className="text-center">
            <p className="text-gray-400 text-sm">Total Cost</p>
            <p className="text-2xl font-bold text-white">{formatPrice(totalCost)}</p>
          </div>
        </div>
        <div className="glass-effect p-4">
          <div className="text-center">
            <p className="text-gray-400 text-sm">Total Profit</p>
            <p className={`text-2xl font-bold ${totalProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {formatPrice(totalProfit)}
            </p>
          </div>
        </div>
        <div className="glass-effect p-4">
          <div className="text-center">
            <p className="text-gray-400 text-sm">Avg Margin</p>
            <p className={`text-2xl font-bold ${avgMargin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
              {avgMargin.toFixed(1)}%
            </p>
          </div>
        </div>
      </div>

      {/* Sales Table */}
      <div className="glass-effect p-6">
        <h3 className="text-xl font-bold text-white mb-4">Sales History</h3>
        
        {sortedItems.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto bg-gray-600/20 rounded-lg flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </div>
            <p className="text-gray-400">No sales yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-600">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Card</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Set</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Condition</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Buy Price</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Sell Price</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Profit</th>
                  <th className="text-right py-3 px-4 text-gray-400 font-medium">Margin</th>
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">Sold Date</th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.map((item) => {
                  const margin = calculateMargin(item.buyPrice, item.sold?.sellPrice || 0);
                  const marginPercent = calculateMarginPercent(item.buyPrice, item.sold?.sellPrice || 0);
                  
                  return (
                    <tr key={item.id} className="border-b border-gray-700/50 hover:bg-white/5 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-pokemon-purple/20 rounded-lg flex items-center justify-center">
                            <span className="text-pokemon-purple font-bold text-sm">
                              {item.name.charAt(0)}
                            </span>
                          </div>
                          <span className="text-white font-medium">{item.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-gray-300">{item.set || 'Unknown'}</td>
                      <td className="py-3 px-4 text-gray-300">{item.condition || 'Unknown'}</td>
                      <td className="py-3 px-4 text-right text-gray-300">{formatPrice(item.buyPrice)}</td>
                      <td className="py-3 px-4 text-right text-green-400 font-medium">
                        {formatPrice(item.sold?.sellPrice || 0)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-medium ${margin >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {formatPrice(margin)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`font-medium ${marginPercent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {marginPercent.toFixed(1)}%
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-300">
                        {item.sold?.sellDate ? new Date(item.sold.sellDate).toLocaleDateString() : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SalesTable;
